import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries } from 'react-query';
import {
  Search, ChevronRight, RefreshCw, Wallet,
  CheckCircle, Clock, XCircle, AlertTriangle, X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { SortableTableHead } from '@kioskgaming/ui';
import { AgentBalanceItem, GameWalletStatistics } from '../../../types';
import {
  QUERY_KEYS,
  useGameWalletProviders,
  useGameWalletStatistics,
  useAgentBalances,
  useAgentBalanceHealthSnapshots,
} from '../../../hooks/useApi';
import { usePermission } from '../../../hooks/useAuth';
import { PERMISSION_LABELS } from '../../../config/permissions';
import { apiClient } from '../../../services/api';
import { cn, formatCurrency } from '../../../utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/shadcn';
import { useFormatDate } from '../../../hooks/useFormatDate';
import useTableSort from '../../../hooks/useTableSort';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Input from '../../ui/Input';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/Table';
import {
  GAME_WALLET_OVERVIEW_SORT_COLUMNS,
  sortGameWalletOverviewRows,
  deriveHealthLabel,
  canShowAgentBalance,
  canSyncAgentBalance,
  type GameWalletOverviewRow,
} from './gameWalletOverviewSort';
import HealthHeartbeatChart from './HealthHeartbeatChart';
import { buildHealthHeartbeatPoints, countLiveHealth } from './healthHeartbeat';

/* ─── Per-provider stats row ─── */

const AGENT_BALANCE_HIDDEN_HINT =
  `Hidden. This value is only available to admins with the ${PERMISSION_LABELS.gameProviderManagement} permission.`;

const HiddenAgentValue: React.FC = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex cursor-help px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        Hidden
      </span>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-xs">
      {AGENT_BALANCE_HIDDEN_HINT}
    </TooltipContent>
  </Tooltip>
);

const ProviderStatsRow: React.FC<{
  row: GameWalletOverviewRow;
  onClick: () => void;
  onSyncComplete: () => void;
  canSync: boolean;
  canViewAgentBalance: boolean;
}> = ({ row, onClick, onSyncComplete, canSync, canViewAgentBalance }) => {
  const fmt = useFormatDate();
  const { provider, stats, statsLoading, healthLoading, agentBalance, healthLabel } = row;
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      const result = await apiClient.syncAgentBalanceOne(provider.id);
      if ((result as any)?.data?.failed > 0) {
        toast.error(`Sync failed for ${provider.name}`);
      } else {
        toast.success(`Synced ${provider.name}`);
      }
      onSyncComplete();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error('Sync already in progress for this provider.');
      } else if (status === 400) {
        toast('This provider does not support agent balance sync.', { icon: 'ℹ️' });
      } else {
        toast.error('Sync failed. Please try again.');
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const counts = stats?.syncStatusCounts;

  const healthBadgeClass: Record<string, string> = {
    'Down risk': 'bg-red-100 text-red-800',
    'Out of sync': 'bg-yellow-100 text-yellow-800',
    Healthy: 'bg-green-100 text-green-800',
    'N/A': 'bg-gray-100 text-gray-700',
  };
  const showAgentMoney = canShowAgentBalance(healthLabel);
  const showSync = canSync && canSyncAgentBalance(agentBalance, provider.status);

  const providerStatusBadge =
    provider.status === 'active'
      ? 'bg-green-100 text-green-800'
      : 'bg-gray-100 text-gray-600';

  const total = stats?.totalWallets ?? 0;

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/40 transition-colors"
      onClick={onClick}
    >
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center font-bold text-primary text-sm shrink-0">
            {provider.code.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="font-medium text-sm">{provider.name}</div>
            <div className="text-xs text-muted-foreground">
              Code: {provider.code}
              {' · '}
              <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold', providerStatusBadge)}>
                {provider.status}
              </span>
            </div>
          </div>
        </div>
      </TableCell>

      <TableCell>
        {statsLoading ? (
          <span className="text-muted-foreground text-xs">…</span>
        ) : (
          <div>
            <span className="tabular-nums font-medium">{total.toLocaleString()}</span>
            {total > 0 && (
              <div className="mt-1 h-1.5 w-24 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary/60"
                  style={{ width: `${Math.min(100, (total / 5000) * 100)}%` }}
                />
              </div>
            )}
          </div>
        )}
      </TableCell>

      <TableCell className="tabular-nums text-sm">
        {statsLoading ? '…' : counts?.in_sync ?? '—'}
      </TableCell>

      <TableCell className="tabular-nums text-sm">
        {statsLoading ? '…' : counts?.pending ?? '—'}
      </TableCell>

      <TableCell className="tabular-nums text-sm">
        {statsLoading ? '…' : (
          <span className={cn(counts?.failed ? 'text-destructive font-semibold' : '')}>
            {counts?.failed ?? '—'}
          </span>
        )}
      </TableCell>

      <TableCell className="text-right tabular-nums text-sm">
        {!canViewAgentBalance ? (
          <HiddenAgentValue />
        ) : healthLoading ? (
          <span className="text-muted-foreground text-xs">…</span>
        ) : showAgentMoney && agentBalance?.balance != null
          ? formatCurrency(agentBalance.balance, agentBalance.currency)
          : <span className="text-muted-foreground">—</span>}
      </TableCell>

      <TableCell className="text-sm text-muted-foreground tabular-nums">
        {!canViewAgentBalance ? (
          <HiddenAgentValue />
        ) : healthLoading
          ? '…'
          : showAgentMoney && agentBalance?.lastSyncedAt
            ? fmt(agentBalance.lastSyncedAt)
            : '—'}
      </TableCell>

      <TableCell>
        {!canViewAgentBalance ? (
          <HiddenAgentValue />
        ) : healthLoading ? (
          <span className="text-muted-foreground text-xs">…</span>
        ) : (
          <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', healthBadgeClass[healthLabel])}>
            {healthLabel}
          </span>
        )}
      </TableCell>

      <TableCell>
        <div className="flex items-center justify-start gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
          >
            View detail <ChevronRight className="h-3 w-3" />
          </button>
          {showSync && (
            <button
              type="button"
              onClick={handleSync}
              disabled={isSyncing}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors',
                isSyncing
                  ? 'border-muted bg-muted text-muted-foreground cursor-not-allowed'
                  : 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
              )}
            >
              <RefreshCw className={cn('h-3 w-3', isSyncing && 'animate-spin')} />
              Sync
            </button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

/* ─── Overview page ─── */

const GameWalletsOverview: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const canViewAgentBalance = usePermission('gameProviderManagement');
  const canSyncProviders = canViewAgentBalance;

  const tableSort = useTableSort({
    columns: GAME_WALLET_OVERVIEW_SORT_COLUMNS,
    paramPrefix: 'overview',
  });

  const headerSort = tableSort.sortBy
    ? { activeKey: tableSort.sortBy, direction: tableSort.sortOrder }
    : { activeKey: null, direction: null };

  const { data: providersData, isLoading: providersLoading, refetch: refetchProviders } = useGameWalletProviders();
  const { data: globalStats, isLoading: globalStatsLoading } = useGameWalletStatistics();
  const { data: agentBalancesData, isLoading: agentBalancesLoading, refetch: refetchAgentBalances } = useAgentBalances(
    { limit: 500 },
    canViewAgentBalance,
  );
  const {
    data: healthSnapshotsData,
    isLoading: healthSnapshotsLoading,
    refetch: refetchHealthSnapshots,
  } = useAgentBalanceHealthSnapshots(
    24,
    canViewAgentBalance,
  );

  const healthHeartbeatPoints = useMemo(() => {
    if (!canViewAgentBalance) return [];
    const live = agentBalancesLoading
      ? null
      : countLiveHealth(agentBalancesData?.data?.items ?? []);
    return buildHealthHeartbeatPoints(healthSnapshotsData?.data?.items ?? [], live);
  }, [
    canViewAgentBalance,
    agentBalancesLoading,
    agentBalancesData,
    healthSnapshotsData,
  ]);

  const allProviders = useMemo(() => providersData?.data?.items ?? [], [providersData]);

  const statsQueries = useQueries(
    allProviders.map((provider) => ({
      queryKey: [QUERY_KEYS.GAME_WALLET_STATISTICS, { gameProviderId: provider.id }],
      queryFn: () => apiClient.getGameWalletStatistics({ gameProviderId: provider.id }),
      staleTime: 5 * 60 * 1000,
    })),
  );

  const agentBalanceByProvider = useMemo(() => {
    const map = new Map<string, AgentBalanceItem>();
    for (const item of agentBalancesData?.data?.items ?? []) {
      map.set(item.gameProviderId, item);
    }
    return map;
  }, [agentBalancesData]);

  const overviewRows = useMemo((): GameWalletOverviewRow[] => {
    return allProviders.map((provider, index) => {
      const query = statsQueries[index];
      const stats = query?.data?.data as GameWalletStatistics | undefined;
      const agentBalance = agentBalanceByProvider.get(provider.id);

      return {
        provider,
        stats,
        statsLoading: query?.isLoading ?? false,
        healthLoading: canViewAgentBalance && agentBalancesLoading,
        agentBalance,
        healthLabel: deriveHealthLabel(agentBalance),
      };
    });
  }, [allProviders, statsQueries, agentBalanceByProvider, agentBalancesLoading, canViewAgentBalance]);

  const visibleRows = useMemo(() => {
    const q = search.toLowerCase().trim();
    let rows = overviewRows;

    if (q) {
      rows = rows.filter(
        (row) =>
          row.provider.name.toLowerCase().includes(q)
          || row.provider.code.toLowerCase().includes(q),
      );
    }

    return sortGameWalletOverviewRows(rows, tableSort.sortBy, tableSort.sortOrder);
  }, [overviewRows, search, tableSort.sortBy, tableSort.sortOrder]);

  const globalCounts = globalStats?.data?.syncStatusCounts;

  const sortableHeader = (key: string, label: string, align?: 'left' | 'right') => (
    <SortableTableHead
      sortable
      label={label}
      sortKey={key}
      activeKey={headerSort.activeKey}
      direction={headerSort.direction}
      onSort={tableSort.toggleSort}
      align={align}
    />
  );

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Game Wallets</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor player game wallets by provider. Sort columns below; transaction-level sort is on each provider&apos;s{' '}
            <strong className="font-medium text-foreground">Transactions</strong> tab.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void refetchProviders();
            if (canViewAgentBalance) {
              void refetchAgentBalances();
              void refetchHealthSnapshots();
            }
          }}
          disabled={providersLoading}
        >
          <RefreshCw className={cn('h-4 w-4 mr-1.5', providersLoading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <WalletKpiStrip
        items={[
          {
            label: 'Providers',
            value: providersLoading ? '…' : formatCount(allProviders.length),
            icon: <Wallet className="h-3.5 w-3.5" />,
            className: 'col-span-3 lg:col-span-1',
          },
          {
            label: 'Total Wallets',
            value: globalStatsLoading ? '…' : formatCount(globalStats?.data?.totalWallets),
            icon: <Wallet className="h-3.5 w-3.5" />,
            className: 'col-span-3 lg:col-span-1 border-r-0 lg:border-r',
          },
          {
            label: 'In Sync',
            value: globalStatsLoading ? '…' : formatCount(globalCounts?.in_sync),
            icon: <CheckCircle className="h-3.5 w-3.5" />,
            valueClassName: 'text-green-700',
            className: 'col-span-2 lg:col-span-1 border-b-0',
          },
          {
            label: 'Pending',
            value: globalStatsLoading ? '…' : formatCount(globalCounts?.pending),
            icon: <Clock className="h-3.5 w-3.5" />,
            valueClassName: 'text-amber-700',
            className: 'col-span-2 lg:col-span-1 border-b-0',
          },
          {
            label: 'Failed',
            value: globalStatsLoading ? '…' : formatCount(globalCounts?.failed),
            icon: <XCircle className="h-3.5 w-3.5" />,
            valueClassName: 'text-destructive',
            className: 'col-span-2 lg:col-span-1 border-b-0',
          },
        ]}
      />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Provider health heartbeat</CardTitle>
          <p className="text-xs text-muted-foreground">
            Healthy, Out of sync, Down risk, and N/A provider counts over the last 24 hours.
          </p>
        </CardHeader>
        <CardContent className="h-[300px] pt-0">
          {!canViewAgentBalance ? (
            <div className="flex h-full items-center justify-center">
              <HiddenAgentValue />
            </div>
          ) : healthSnapshotsLoading && !healthSnapshotsData ? (
            <div className="flex h-full items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <HealthHeartbeatChart data={healthHeartbeatPoints} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Provider wallet summary</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Click a row to open provider detail. Sort applies to the full filtered list on this page.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-end mt-3">
            <div className="flex-1 min-w-[180px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search provider name or code…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-8"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {providersLoading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <AlertTriangle className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No providers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table aria-busy={statsQueries.some((q) => q.isFetching) || undefined}>
                <TableHeader>
                  <TableRow>
                    {sortableHeader('provider', 'Provider')}
                    {sortableHeader('totalWallets', 'Total Wallets')}
                    {sortableHeader('inSync', 'In-sync Wallets')}
                    {sortableHeader('pending', 'Pending Wallets')}
                    {sortableHeader('failed', 'Failed Wallets')}
                    {sortableHeader('balance', 'Game Agent Balance', 'right')}
                    {sortableHeader('lastSynced', 'Last Synced')}
                    {sortableHeader('health', 'Health Status')}
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map((row) => (
                    <ProviderStatsRow
                      key={row.provider.id}
                      row={row}
                      onClick={() => navigate(`/wallets/${row.provider.id}`)}
                      onSyncComplete={() => void refetchAgentBalances()}
                      canSync={canSyncProviders}
                      canViewAgentBalance={canViewAgentBalance}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!providersLoading && visibleRows.length > 0 && (
            <div className="flex items-center justify-between px-6 py-3 border-t text-xs text-muted-foreground">
              <span>Showing {visibleRows.length} of {allProviders.length} providers</span>
              {tableSort.sortBy && (
                <span>
                  Sorted by {tableSort.getColumn(tableSort.sortBy)?.label ?? tableSort.sortBy}{' '}
                  ({tableSort.sortOrder === 'ASC' ? 'ascending' : 'descending'})
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </TooltipProvider>
  );
};

function formatCount(value?: number): string {
  return value == null ? '—' : value.toLocaleString('en-US');
}

const WalletKpiStrip: React.FC<{
  items: Array<{
    label: string;
    value: string;
    icon: React.ReactNode;
    valueClassName?: string;
    className?: string;
  }>;
}> = ({ items }) => (
  <Card className="overflow-hidden">
    <div className="grid grid-cols-6 lg:grid-cols-5">
      {items.map((item) => (
        <div
          key={item.label}
          className={cn(
            'flex min-w-0 items-center gap-3 border-b border-r border-border/70 px-4 py-3 last:border-r-0 lg:border-b-0',
            item.className,
          )}
        >
          <span className="text-muted-foreground shrink-0">{item.icon}</span>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {item.label}
            </p>
            <p className={cn('text-xl font-bold tabular-nums tracking-tight', item.valueClassName)}>
              {item.value}
            </p>
          </div>
        </div>
      ))}
    </div>
  </Card>
);

export default GameWalletsOverview;
